import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const AdminDashboard = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState({
    advisorFirms: 0,
    users: 0,
    plans: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [billingEdits, setBillingEdits] = useState({});
  const [savingBillingId, setSavingBillingId] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [planEdits, setPlanEdits] = useState({});
  const [savingPlanId, setSavingPlanId] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [formData, setFormData] = useState({
    invitationType: 'advisor',
    firmName: '',
    firstName: '',
    lastName: '',
    email: '',
    advisorRole: 'advisor_admin',
    lawFirmRole: 'attorney',
    statesServed: ''
  });

  useEffect(() => {
    const fetchStats = async () => {
      if (profile?.role !== 'platform_admin') {
        setStatsLoading(false);
        return;
      }

      try {
        setStatsLoading(true);
        const [firmRes, userRes, planRes, billingRes, subscriptionPlanRes] = await Promise.all([
          supabase.from('advisor_firms').select('*', { count: 'exact', head: true }),
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('matters').select('*', { count: 'exact', head: true }),
          supabase
            .from('advisor_billing_accounts')
            .select(`
              id,
              account_category,
              subscription_state,
              access_state,
              billing_interval,
              actual_amount_cents,
              included_monthly_submissions,
              bonus_submissions_current_period,
              trial_ends_at,
              stripe_environment,
              admin_notes,
              complimentary_can_submit_real_plans,
              advisor_firm:advisor_firms(firm_name),
              plan:subscription_plans(display_name, plan_key)
            `)
            .order('created_at', { ascending: false })
            .limit(10),
          supabase
            .from('subscription_plans')
            .select(`
              id,
              plan_key,
              display_name,
              standard_monthly_amount_cents,
              standard_annual_amount_cents,
              stripe_test_monthly_price_id,
              stripe_test_annual_price_id,
              stripe_live_monthly_price_id,
              stripe_live_annual_price_id
            `)
            .eq('audience', 'advisor')
            .order('standard_monthly_amount_cents', { ascending: true })
        ]);

        setStats({
          advisorFirms: firmRes.count || 0,
          users: userRes.count || 0,
          plans: planRes.count || 0
        });
        if (billingRes.error) {
          setBillingError(billingRes.error.message || 'Unable to load billing accounts.');
        } else {
          setBillingAccounts(billingRes.data || []);
          setBillingEdits(
            (billingRes.data || []).reduce((acc, account) => ({
              ...acc,
              [account.id]: {
                account_category: account.account_category || 'standard',
                subscription_state: account.subscription_state || 'trialing',
                access_state: account.access_state || 'trialing',
                billing_interval: account.billing_interval || 'monthly',
                actual_amount_cents: account.actual_amount_cents ?? 0,
                included_monthly_submissions: account.included_monthly_submissions ?? 4,
                bonus_submissions_current_period: account.bonus_submissions_current_period ?? 0,
                trial_ends_at: account.trial_ends_at ? account.trial_ends_at.slice(0, 10) : '',
                stripe_environment: account.stripe_environment || 'test',
                complimentary_can_submit_real_plans: Boolean(account.complimentary_can_submit_real_plans),
                admin_notes: account.admin_notes || ''
              }
            }), {})
          );
          setBillingError(null);
        }
        if (subscriptionPlanRes.error) {
          setBillingError(subscriptionPlanRes.error.message || 'Unable to load subscription plans.');
        } else {
          setSubscriptionPlans(subscriptionPlanRes.data || []);
          setPlanEdits(
            (subscriptionPlanRes.data || []).reduce((acc, plan) => ({
              ...acc,
              [plan.id]: {
                stripe_test_monthly_price_id: plan.stripe_test_monthly_price_id || '',
                stripe_test_annual_price_id: plan.stripe_test_annual_price_id || '',
                stripe_live_monthly_price_id: plan.stripe_live_monthly_price_id || '',
                stripe_live_annual_price_id: plan.stripe_live_annual_price_id || ''
              }
            }), {})
          );
        }
      } catch (err) {
        console.error('Admin stats error:', err);
        setBillingError(err.message || 'Unable to load billing overview.');
      } finally {
        setStatsLoading(false);
        setBillingLoading(false);
      }
    };

    fetchStats();
  }, [profile]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const formatState = (value) => value?.replaceAll('_', ' ') || 'Unknown';

  const handleBillingEditChange = (accountId, field, value) => {
    setBillingEdits((current) => ({
      ...current,
      [accountId]: {
        ...(current[accountId] || {}),
        [field]: value
      }
    }));
  };

  const handlePlanEditChange = (planId, field, value) => {
    setPlanEdits((current) => ({
      ...current,
      [planId]: {
        ...(current[planId] || {}),
        [field]: value
      }
    }));
  };

  const handleSaveSubscriptionPlan = async (planId) => {
    const edit = planEdits[planId];
    if (!edit || savingPlanId) return;

    try {
      setSavingPlanId(planId);
      setBillingError(null);

      const { data, error } = await supabase.rpc('platform_admin_update_subscription_plan_prices', {
        p_plan_id: planId,
        p_stripe_test_monthly_price_id: edit.stripe_test_monthly_price_id || null,
        p_stripe_test_annual_price_id: edit.stripe_test_annual_price_id || null,
        p_stripe_live_monthly_price_id: edit.stripe_live_monthly_price_id || null,
        p_stripe_live_annual_price_id: edit.stripe_live_annual_price_id || null
      });

      if (error) throw error;

      const updated = Array.isArray(data) ? data[0] : data;
      setSubscriptionPlans((current) => current.map((plan) => (
        plan.id === planId ? { ...plan, ...(updated || {}) } : plan
      )));
    } catch (err) {
      console.error('Subscription plan price update error:', err);
      setBillingError(err.message || 'Unable to update subscription plan prices.');
    } finally {
      setSavingPlanId(null);
    }
  };

  const handleSaveBillingAccount = async (accountId) => {
    const edit = billingEdits[accountId];
    if (!edit || savingBillingId) return;

    try {
      setSavingBillingId(accountId);
      setBillingError(null);

      const { data, error } = await supabase.rpc('platform_admin_update_advisor_billing_account', {
        p_account_id: accountId,
        p_account_category: edit.account_category,
        p_subscription_state: edit.subscription_state,
        p_access_state: edit.access_state,
        p_billing_interval: edit.billing_interval,
        p_actual_amount_cents: Number(edit.actual_amount_cents) || 0,
        p_included_monthly_submissions: Number(edit.included_monthly_submissions) || 0,
        p_bonus_submissions_current_period: Number(edit.bonus_submissions_current_period) || 0,
        p_trial_ends_at: edit.trial_ends_at ? new Date(`${edit.trial_ends_at}T23:59:59`).toISOString() : null,
        p_complimentary_can_submit_real_plans: Boolean(edit.complimentary_can_submit_real_plans),
        p_admin_notes: edit.admin_notes || null,
        p_stripe_environment: edit.stripe_environment || 'test'
      });

      if (error) throw error;

      const updated = Array.isArray(data) ? data[0] : data;
      setBillingAccounts((current) => current.map((account) => (
        account.id === accountId ? { ...account, ...(updated || {}) } : account
      )));
    } catch (err) {
      console.error('Billing account update error:', err);
      setBillingError(err.message || 'Unable to update billing account.');
    } finally {
      setSavingBillingId(null);
    }
  };

  const copyInviteLink = async (link) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setInviteCopied(true);
      }
    } catch (err) {
      console.warn('Unable to copy law firm invite link:', err);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteError(null);
    setInviteLink('');
    setInviteCopied(false);

    const states = formData.statesServed
      .split(',')
      .map((state) => state.trim())
      .filter(Boolean);

    try {
      const rpcName = formData.invitationType === 'advisor'
        ? 'create_advisor_firm_invitation'
        : 'create_law_firm_invitation';
      const rpcArgs = formData.invitationType === 'advisor'
        ? {
            p_firm_name: formData.firmName,
            p_email: formData.email,
            p_first_name: formData.firstName,
            p_last_name: formData.lastName,
            p_role: formData.advisorRole
          }
        : {
            p_firm_name: formData.firmName,
            p_email: formData.email,
            p_first_name: formData.firstName,
            p_last_name: formData.lastName,
            p_role: formData.lawFirmRole,
            p_states_served: states.length > 0 ? states : null
          };

      const { data, error } = await supabase.rpc(rpcName, rpcArgs);

      if (error) throw error;

      const invitation = Array.isArray(data) ? data[0] : data;
      if (!invitation?.invite_path) {
        throw new Error('Invitation was created, but no invite path was returned.');
      }

      const link = `${window.location.origin}${invitation.invite_path}`;
      setInviteLink(link);
      await copyInviteLink(link);
    } catch (err) {
      console.error('Create platform invitation error:', err);
      setInviteError(err.message || 'Unable to create platform invitation.');
    } finally {
      setInviteLoading(false);
    }
  };

  if (profile?.role !== 'platform_admin') {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
        <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-bold text-slate-900">Platform admin access required</h1>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Platform Administration</h1>
        <p className="mt-1 text-sm text-slate-500">System overview and management</p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {[
          { name: 'Total Advisor Firms', stat: stats.advisorFirms, icon: FiIcons.FiBriefcase },
          { name: 'Total Users', stat: stats.users, icon: FiIcons.FiUsers },
          { name: 'Total Plans', stat: stats.plans, icon: FiIcons.FiFileText },
        ].map((item) => (
          <div key={item.name} className="bg-white overflow-hidden shadow rounded-lg border border-slate-200">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <SafeIcon icon={item.icon} className="h-6 w-6 text-slate-400" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">{item.name}</dt>
                    <dd className="text-2xl font-semibold text-slate-900">{statsLoading ? '...' : item.stat}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg border border-slate-200">
          <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
            <h3 className="text-lg leading-6 font-medium text-slate-900">Invite Platform User</h3>
          </div>
          <form onSubmit={handleCreateInvitation} className="p-6 space-y-5">
            {inviteError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <div className="flex">
                  <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
                  <p className="text-sm text-red-700 font-medium">{inviteError}</p>
                </div>
              </div>
            )}

            {inviteLink && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <h4 className="text-sm font-bold text-blue-900">Invitation link created</h4>
                <p className="mt-2 text-xs font-mono text-blue-900 break-all bg-white/70 rounded border border-blue-100 px-2 py-1">
                  {inviteLink}
                </p>
                <button
                  type="button"
                  onClick={() => copyInviteLink(inviteLink)}
                  className="mt-3 inline-flex items-center px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md bg-white text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <SafeIcon icon={inviteCopied ? FiIcons.FiCheck : FiIcons.FiCopy} className="mr-2 h-4 w-4" />
                  {inviteCopied ? 'Copied' : 'Copy Link'}
                </button>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invitation Type *</label>
              <select
                name="invitationType"
                required
                value={formData.invitationType}
                onChange={handleChange}
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
              >
                <option value="advisor">Financial Advisor Firm</option>
                <option value="law_firm">Law Firm</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {formData.invitationType === 'advisor' ? 'Advisor Firm Name *' : 'Law Firm Name *'}
              </label>
              <input
                name="firmName"
                type="text"
                required
                value={formData.firmName}
                onChange={handleChange}
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                <input
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                <input
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                <select
                  name={formData.invitationType === 'advisor' ? 'advisorRole' : 'lawFirmRole'}
                  required
                  value={formData.invitationType === 'advisor' ? formData.advisorRole : formData.lawFirmRole}
                  onChange={handleChange}
                  className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
                >
                  {formData.invitationType === 'advisor' ? (
                    <>
                      <option value="advisor_admin">Advisor Admin</option>
                      <option value="advisor_user">Advisor User</option>
                    </>
                  ) : (
                    <>
                      <option value="attorney">Attorney</option>
                      <option value="legal_staff">Legal Staff</option>
                    </>
                  )}
                </select>
              </div>
              {formData.invitationType === 'law_firm' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">States Served</label>
                  <input
                    name="statesServed"
                    type="text"
                    value={formData.statesServed}
                    onChange={handleChange}
                    placeholder="Florida, Georgia"
                    className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                  />
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-100 flex justify-end">
              <button
                type="submit"
                disabled={inviteLoading}
                className="inline-flex items-center px-5 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-50 transition-colors"
              >
                {inviteLoading ? (
                  <>
                    <SafeIcon icon={FiIcons.FiLoader} className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Creating...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiIcons.FiSend} className="-ml-1 mr-2 h-4 w-4" />
                    Create Invite Link
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white shadow rounded-lg border border-slate-200">
          <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
            <h3 className="text-lg leading-6 font-medium text-slate-900">Law Firms</h3>
          </div>
          <div className="p-6 text-center text-slate-500 text-sm">
            Law firm list management is planned for the next admin sprint.
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
          <h3 className="text-lg leading-6 font-medium text-slate-900">Stripe Plan Prices</h3>
          <p className="mt-1 text-sm text-slate-500">Save Stripe Price IDs before advisors activate billing through Checkout.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50/70">
              <tr>
                <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Plan</th>
                <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Test Prices</th>
                <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Live Prices</th>
                <th className="px-6 py-3 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {subscriptionPlans.map((plan) => {
                const edit = planEdits[plan.id] || {};

                return (
                  <tr key={plan.id} className="align-top hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-slate-900">{plan.display_name}</div>
                      <div className="text-xs text-slate-500">{formatMoney(plan.standard_monthly_amount_cents)} monthly / {formatMoney(plan.standard_annual_amount_cents)} annual</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 min-w-[360px]">
                        <input
                          type="text"
                          value={edit.stripe_test_monthly_price_id || ''}
                          onChange={(e) => handlePlanEditChange(plan.id, 'stripe_test_monthly_price_id', e.target.value)}
                          placeholder="Test monthly price ID"
                          className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                        />
                        <input
                          type="text"
                          value={edit.stripe_test_annual_price_id || ''}
                          onChange={(e) => handlePlanEditChange(plan.id, 'stripe_test_annual_price_id', e.target.value)}
                          placeholder="Test annual price ID"
                          className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 min-w-[360px]">
                        <input
                          type="text"
                          value={edit.stripe_live_monthly_price_id || ''}
                          onChange={(e) => handlePlanEditChange(plan.id, 'stripe_live_monthly_price_id', e.target.value)}
                          placeholder="Live monthly price ID"
                          className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                        />
                        <input
                          type="text"
                          value={edit.stripe_live_annual_price_id || ''}
                          onChange={(e) => handlePlanEditChange(plan.id, 'stripe_live_annual_price_id', e.target.value)}
                          placeholder="Live annual price ID"
                          className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleSaveSubscriptionPlan(plan.id)}
                        disabled={savingPlanId === plan.id}
                        className="inline-flex items-center px-3 py-2 rounded-md bg-slate-800 text-white text-xs font-bold uppercase tracking-widest hover:bg-slate-900 disabled:opacity-50"
                      >
                        {savingPlanId === plan.id ? (
                          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-4 w-4" />
                        ) : (
                          'Save'
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-slate-200 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg leading-6 font-medium text-slate-900">Advisor Billing Accounts</h3>
            <p className="mt-1 text-sm text-slate-500">Early Phase 7 account, trial, and usage foundation.</p>
          </div>
          <span className="px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Stripe test first
          </span>
        </div>

        {billingError && (
          <div className="m-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
            {billingError}
          </div>
        )}

        {billingLoading ? (
          <div className="py-12 text-center text-slate-300">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 mx-auto" />
          </div>
        ) : billingAccounts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/70">
                <tr>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Advisor Firm</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Plan</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Account</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Usage & Price</th>
                  <th className="px-6 py-3 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {billingAccounts.map((account) => {
                  const edit = billingEdits[account.id] || {};

                  return (
                    <tr key={account.id} className="hover:bg-slate-50 transition-colors align-top">
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-slate-900">{account.advisor_firm?.firm_name || 'Unknown firm'}</div>
                        <div className="text-xs text-slate-500">{account.trial_ends_at ? `Trial ends ${new Date(account.trial_ends_at).toLocaleDateString()}` : 'No trial end'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-700">{account.plan?.display_name || 'No plan'}</div>
                        <div className="text-xs text-slate-500">{formatMoney(account.actual_amount_cents)} / {account.billing_interval || 'No interval'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 min-w-[640px]">
                          <select
                            value={edit.account_category || 'standard'}
                            onChange={(e) => handleBillingEditChange(account.id, 'account_category', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border bg-white"
                          >
                            <option value="standard">Standard</option>
                            <option value="early_adopter">Early Adopter</option>
                            <option value="promotional">Promotional</option>
                            <option value="complimentary">Complimentary</option>
                            <option value="grandfathered">Grandfathered</option>
                            <option value="internal_test">Internal Test</option>
                          </select>
                          <select
                            value={edit.subscription_state || 'trialing'}
                            onChange={(e) => handleBillingEditChange(account.id, 'subscription_state', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border bg-white"
                          >
                            <option value="trialing">Trialing</option>
                            <option value="active">Active</option>
                            <option value="past_due">Past Due</option>
                            <option value="grace_period">Grace Period</option>
                            <option value="restricted">Restricted</option>
                            <option value="canceled">Canceled</option>
                            <option value="complimentary">Complimentary</option>
                            <option value="grandfathered">Grandfathered</option>
                            <option value="internal_test">Internal Test</option>
                            <option value="incomplete_signup">Incomplete Signup</option>
                          </select>
                          <select
                            value={edit.access_state || 'trialing'}
                            onChange={(e) => handleBillingEditChange(account.id, 'access_state', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border bg-white"
                          >
                            <option value="trialing">Trialing</option>
                            <option value="active">Active</option>
                            <option value="grace_period">Grace Period</option>
                            <option value="restricted">Restricted</option>
                            <option value="canceled">Canceled</option>
                            <option value="complimentary">Complimentary</option>
                            <option value="grandfathered">Grandfathered</option>
                            <option value="internal_test">Internal Test</option>
                            <option value="incomplete_signup">Incomplete Signup</option>
                          </select>
                          <select
                            value={edit.stripe_environment || 'test'}
                            onChange={(e) => handleBillingEditChange(account.id, 'stripe_environment', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border bg-white"
                          >
                            <option value="test">Stripe Test</option>
                            <option value="live">Stripe Live</option>
                          </select>
                        </div>
                        <p className="mt-2 text-xs text-slate-500 capitalize">
                          Current: {formatState(account.account_category)} / {formatState(account.access_state)} / Stripe {account.stripe_environment}
                        </p>
                        <label className="mt-2 flex items-center gap-2 text-xs font-medium text-slate-600">
                          <input
                            type="checkbox"
                            checked={Boolean(edit.complimentary_can_submit_real_plans)}
                            onChange={(e) => handleBillingEditChange(account.id, 'complimentary_can_submit_real_plans', e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-slate-800"
                          />
                          Complimentary can submit real plans
                        </label>
                      </td>
                      <td className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 min-w-[540px]">
                          <input
                            type="number"
                            min="0"
                            value={edit.included_monthly_submissions ?? 4}
                            onChange={(e) => handleBillingEditChange(account.id, 'included_monthly_submissions', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                            aria-label="Included submissions"
                          />
                          <input
                            type="number"
                            min="0"
                            value={edit.bonus_submissions_current_period ?? 0}
                            onChange={(e) => handleBillingEditChange(account.id, 'bonus_submissions_current_period', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                            aria-label="Bonus submissions"
                          />
                          <input
                            type="number"
                            min="0"
                            value={edit.actual_amount_cents ?? 0}
                            onChange={(e) => handleBillingEditChange(account.id, 'actual_amount_cents', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                            aria-label="Actual amount in cents"
                          />
                          <select
                            value={edit.billing_interval || 'monthly'}
                            onChange={(e) => handleBillingEditChange(account.id, 'billing_interval', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border bg-white"
                          >
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                          </select>
                          <input
                            type="date"
                            value={edit.trial_ends_at || ''}
                            onChange={(e) => handleBillingEditChange(account.id, 'trial_ends_at', e.target.value)}
                            className="block w-full text-xs border-slate-300 rounded-md py-2 px-2 border"
                            aria-label="Trial ends at"
                          />
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          Included / bonus / cents / interval / trial end
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleSaveBillingAccount(account.id)}
                          disabled={savingBillingId === account.id}
                          className="inline-flex items-center px-3 py-2 rounded-md bg-slate-800 text-white text-xs font-bold uppercase tracking-widest hover:bg-slate-900 disabled:opacity-50"
                        >
                          {savingBillingId === account.id ? (
                            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-4 w-4" />
                          ) : (
                            'Save'
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-500 text-sm">
            No advisor billing accounts found.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
